import * as Dfns from 'date-fns/fp';
import { TEST_CREATIVES } from '@blisspointmedia/bpm-types/dist/TestCompanies';
import { getFormattedValue } from '@Components/NumberFormatter/NumberFormatter';
import { CreativeMap } from '@src/redux/creative';
import { awaitJSON, LinearLambdaFetch } from '@src/utils/fetch-utils';
import { convertSVGStringToPNGURI } from '@src/utils/download-utils';
import { DATE_FORMAT } from '@src/utils/date-utils';
import {
  CreativeInfo,
  SpikeChartData,
  SpotCoordinate,
  SpotTableRow
} from './spike-chart-constants';

// The default week selection: the start of the current ISO week, unless that's also
// "today" (the week just started), in which case fall back to last week so there's
// data to show.
export const getDefaultWeek = (now: Date = new Date()): string => {
  const today = Dfns.format(DATE_FORMAT, now);
  const startOfWeek = Dfns.format(DATE_FORMAT, Dfns.startOfISOWeek(now));

  if (today === startOfWeek) {
    return Dfns.format(DATE_FORMAT, Dfns.subWeeks(1, Dfns.startOfISOWeek(now)));
  }

  return startOfWeek;
};

// Weeks after this are in the future and can't be selected.
export const getCutoffDate = (now: Date = new Date()): string =>
  Dfns.format(DATE_FORMAT, Dfns.startOfISOWeek(now));

export const getXAxisTicks = (
  data: SpikeChartData[] | undefined,
  tickCount?: number
): number[] | null => {
  if (!data || data.length === 0) {
    return null;
  }

  const ticks: number[] = [];
  const seenDateTicks = new Set<string>();
  const maxTicks = tickCount ?? 0;

  for (const item of data) {
    const dateTick = Dfns.format('MMM do', new Date(item.formattedTime).getTime());
    if (!seenDateTicks.has(dateTick) && ticks.length < maxTicks) {
      ticks.push(new Date(item.formattedTime).getTime());
      seenDateTicks.add(dateTick);
    }
  }
  return ticks;
};

export const getYAxisDomain = (
  data: { time: number; kpiCount: number }[],
  left: number,
  right: number
): [number, number] => {
  const refData = data.filter(item => item.time >= left && item.time <= right);
  let bottom = refData[0].kpiCount;
  let top = refData[0].kpiCount;

  for (const item of refData) {
    if (item.kpiCount > top) {
      top = item.kpiCount;
    }
    if (item.kpiCount < bottom) {
      bottom = item.kpiCount;
    }
  }

  return [bottom, Math.ceil(top * 1.05)];
};

// recharts v3 rebuilds its axis-scale hooks' return value on every render even when
// the scale itself is unchanged; comparing computed coordinates by value (rather than
// trusting the scale's reference identity) stops a spurious setState from re-triggering
// a render loop.
export const coordinatesEqual = (
  a: Record<number, SpotCoordinate>,
  b: Record<number, SpotCoordinate>
): boolean => {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) {
    return false;
  }
  return aKeys.every(
    key => a[Number(key)].x === b[Number(key)]?.x && a[Number(key)].y === b[Number(key)]?.y
  );
};

// PNG export of the currently rendered chart SVG.
export const pngUri = (id: string): string => {
  const chart = document.querySelectorAll(`#${id} svg`);
  const serializer = new XMLSerializer();
  const svgData = serializer.serializeToString(chart[0]);

  return convertSVGStringToPNGURI(svgData);
};

export const getSpikeChartData = async (
  kpi: string,
  week: string
): Promise<SpikeChartData[] | null> => {
  const res = await LinearLambdaFetch('/spike_chart', { params: { kpi, week } });
  const spikeData = await awaitJSON<SpikeChartData[]>(res);
  const spikeChartData: SpikeChartData[] = spikeData.map(item => {
    const time = Dfns.parseISO(item.formattedTime).getTime();
    const creativeInfo: CreativeInfo[] =
      item.creativeInfo?.map(airing => ({ ...airing, timeslot: time })) ?? [];
    return creativeInfo.length ? { ...item, time, creativeInfo } : { ...item, time };
  });

  return spikeChartData.length > 1
    ? [...spikeChartData].sort((a, b) => {
        const aDate = new Date(Dfns.parseISO(a.formattedTime).getTime());
        const bDate = new Date(Dfns.parseISO(b.formattedTime).getTime());
        return (
          Date.UTC(aDate.getFullYear(), aDate.getMonth(), aDate.getDate()) -
          Date.UTC(bDate.getFullYear(), bDate.getMonth(), bDate.getDate())
        );
      })
    : spikeChartData;
};

// Randomly swaps in a fake creative for test/prospect companies so real client
// creative names/thumbnails are never displayed.
export const getTestIsci = (creative: string): string | undefined => {
  const info = TEST_CREATIVES.find(testCreative => testCreative.name === creative);
  return info?.isci;
};

// The spike-chart data's `creative` field is a display name with spaces stripped,
// not an isci — look up the matching creativeMap entry by that stripped name and
// return its isci (not its `.file`, which is a raw asset filename).
export const findIsciByCreativeName = (
  creativeMap: CreativeMap,
  creativeName: string
): string | undefined =>
  Object.values(creativeMap).find(info => info.name.replace(/\s/g, '') === creativeName)?.isci;

// Shapes the raw /spike_chart response into chart-ready data: parses the ISO
// timestamp into a millisecond `time`, and stamps each spot's creativeInfo with
// that timeslot (substituting a fake creative for test companies).
export const buildSpikeChartData = (
  spikeData: SpikeChartData[],
  isTestCompany: boolean
): SpikeChartData[] =>
  spikeData.map(item => {
    const time = Dfns.parseISO(item.formattedTime.split(' ')[0]).getTime();
    const creativeInfo: CreativeInfo[] =
      item.creativeInfo?.map(airing => {
        if (!isTestCompany) {
          return { ...airing, timeslot: time };
        }
        const random = Math.floor(Math.random() * TEST_CREATIVES.length);
        return { ...airing, timeslot: time, creative: TEST_CREATIVES[random].name };
      }) ?? [];
    return creativeInfo.length ? { ...item, time, creativeInfo } : { ...item, time };
  });

// Flattens each spot's creativeInfo into one row per airing, alternating an `odd`
// flag per spot-time group for zebra-striping the table.
export const buildSpotTableData = (data: SpikeChartData[] | undefined): SpotTableRow[] => {
  if (!data) {
    return [];
  }

  const rows: SpotTableRow[] = [];
  let odd = false;
  for (const item of data.filter(entry => entry.dotValue)) {
    for (const spot of item.creativeInfo ?? []) {
      rows.push({ ...spot, odd });
    }
    odd = !odd;
  }
  return rows;
};

interface SpikeCsvRow {
  'Spot Time': string;
  Station: string;
  Avail: string;
  Program: string;
  Creative: string;
  Length: number;
  Cost: string;
  Spike: number;
}

export const formatSpikeCsvRows = (tableData: SpotTableRow[]): SpikeCsvRow[] =>
  tableData.map(row => ({
    'Spot Time': row.time,
    Station: row.network,
    Avail: row.avail,
    Program: row.program,
    Creative: row.creative,
    Length: row.length,
    Cost: getFormattedValue({ value: row.cost, type: '#0' }),
    Spike: row.kpi
  }));
